import { Logger } from '_common/services';
import { ContentStylesHandler } from './ContentStylesHandler';
import { DocumentStylesHandler } from './DocumentStylesHandler';
import { SelectionStatusHandler } from './SelectionStatusHandler';
import { StylesUtils } from './StylesUtils';

export class StylesManager {
  editionContext: Editor.Edition.Context;
  stylesContext: Editor.Edition.Styles.Context;

  contentStyles: Editor.Edition.ContentStylesHandler;
  documentStyles: Editor.Edition.DocumentStylesHandler;
  selectionStatus: Editor.Edition.SelectionStatusHandler;
  stylesUtils: Editor.Edition.StylesUtils;

  constructor(editionContext: Editor.Edition.Context) {
    this.editionContext = editionContext;

    this.stylesContext = {
      debug: false,
    };

    this.contentStyles = new ContentStylesHandler(editionContext, this.stylesContext);
    this.documentStyles = new DocumentStylesHandler(editionContext, this.stylesContext);
    this.selectionStatus = new SelectionStatusHandler(editionContext, this.stylesContext);

    this.stylesUtils = new StylesUtils(editionContext, this.stylesContext);

    this.stylesContext.contentStyles = this.contentStyles;
    this.stylesContext.documentStyles = this.documentStyles;
    this.stylesContext.selectionStatus = this.selectionStatus;
    this.stylesContext.stylesUtils = this.stylesUtils;

    this.onSelectionUpdate = this.onSelectionUpdate.bind(this);
  }

  initialize() {
    this.editionContext.DataManager?.on('SELECTION_UPDATED', this.onSelectionUpdate);

    this.contentStyles.initialize();
    this.documentStyles.initialize();
    this.selectionStatus.initialize();
  }

  destroy() {
    this.editionContext.DataManager?.off('SELECTION_UPDATED', this.onSelectionUpdate);

    this.contentStyles.destroy();
    this.documentStyles.destroy();
    this.selectionStatus.destroy();
  }

  get BlockStylesParser() {
    return this.stylesUtils.BlockStylesParser;
  }

  get InlineStylesParser() {
    return this.stylesUtils.InlineStylesParser;
  }

  private onSelectionUpdate(
    ranges: Editor.Selection.RangeData[],
    source: Realtime.Core.RealtimeSourceType,
  ) {
    if (this.stylesContext.debug) {
      Logger.trace('[StylesManager] onSelectionUpdate', ranges, source);
    }
    // if (this.stylesContext.debug) {
    //   logger.trace('StylesManager onSelectionUpdate');
    // }
    if (source === 'LOCAL_RENDER' || source === 'LOCAL_RENDER_OLD' || source === true) {
      // reset pending styles
      this.contentStyles.resetPendingStyles();
      this.selectionStatus.debounceCheckSelectionStatus();
    }
  }

  // #####################################
  // #region Styles Utils
  // #####################################
  getStylesAppliedToContent(
    ...args: Parameters<Editor.Edition.StylesUtils['getStylesAppliedToContent']>
  ): Editor.Edition.StylesMap {
    return this.stylesUtils.getStylesAppliedToContent(...args);
  }

  getStylesAppliedToData(
    ...args: Parameters<Editor.Edition.StylesUtils['getStylesAppliedToData']>
  ) {
    return this.stylesUtils.getStylesAppliedToData(...args);
  }

  getStylesAppliedToSelection(
    ...args: Parameters<Editor.Edition.StylesUtils['getStylesAppliedToSelection']>
  ) {
    return this.stylesUtils.getStylesAppliedToSelection(...args);
  }

  // #endregion

  // #####################################
  // #region Content Styles
  // #####################################
  havePendingStyles() {
    return this.contentStyles.havePendingStyles();
  }

  addStylesToApply(styles: Editor.Edition.StylesMap) {
    const styleKeys = Object.typedKeys(styles);

    for (let s = 0; s < styleKeys.length; s++) {
      const style = styleKeys[s];
      const value = styles[style];

      if (value != null) {
        this.contentStyles.addStyleToApply(style, value);
      }
    }
  }

  applyPendingStyles(
    range?: Editor.Selection.JsonRange,
    options?: Editor.Edition.StylesCommandOptions,
  ) {
    return this.contentStyles.applyPendingStyles(range, options);
  }

  toggleStyleValue(...args: Parameters<Editor.Edition.ContentStylesHandler['toggleStyleValue']>) {
    return this.contentStyles.toggleStyleValue(...args);
  }

  removeStyleValue(...args: Parameters<Editor.Edition.ContentStylesHandler['removeStyleValue']>) {
    return this.contentStyles.removeStyleValue(...args);
  }

  clearFormat(...args: Parameters<Editor.Edition.ContentStylesHandler['clearFormat']>) {
    return this.contentStyles.clearFormat(...args);
  }

  async identationCurrentSelection(indentationStyles: Editor.Edition.IndentationStylesMap) {
    if (this.stylesContext.debug) {
      logger.debug('StylesManager identationCurrentSelection', indentationStyles);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'APPLY_STYLES_COMMAND',
      indentationStyles,
      undefined,
      { untrackedActionWarning: true },
    );

    if (command) {
      await command.exec();
    }
  }

  async applyParagraphBackgroundColor(color: Editor.Edition.StylesMap['color']) {
    if (this.stylesContext.debug) {
      logger.debug('StylesManager applyParagraphBackgroundColor', color);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'APPLY_STYLES_COMMAND',
      {
        paragraphColor: color,
      },
      undefined,
      { untrackedActionWarning: true },
    );

    if (command) {
      await command.exec();
    }
  }

  async setLinePaginationProperties(paginationStylesMap: Editor.Edition.PaginationStylesMap) {
    if (this.stylesContext.debug) {
      logger.debug('StylesManager setLinePaginationProperties', paginationStylesMap);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'APPLY_STYLES_COMMAND',
      paginationStylesMap,
      undefined,
      { untrackedActionWarning: true },
    );

    if (command) {
      await command.exec();
    }
  }

  async lineSpaceCurrentSelection(spacingStylesMap: Editor.Edition.SpacingStylesMap) {
    if (this.stylesContext.debug) {
      logger.debug('StylesManager lineSpaceCurrentSelection', spacingStylesMap);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'APPLY_STYLES_COMMAND',
      spacingStylesMap,
      undefined,
      { untrackedActionWarning: true },
    );

    if (command) {
      await command.exec();
    }
  }

  async alignCurrentSelection(value: Editor.Edition.StylesMap['alignment']) {
    if (this.stylesContext.debug) {
      logger.debug('StylesManager alignCurrentSelection', value);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'APPLY_STYLES_COMMAND',
      {
        alignment: value,
      },
      undefined,
      { useSelectedCells: false, untrackedActionWarning: true },
    );

    if (command) {
      await command.exec();
    }
  }

  async indentContent(
    indentOption: Editor.Edition.IndentOption = 'indent',
    rangeToApply?: Editor.Selection.JsonRange,
    options?: Editor.Edition.StylesCommandOptions,
  ) {
    if (this.stylesContext.debug) {
      logger.debug('StylesManager indentContent', indentOption);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'INDENT_CONTENT',
      indentOption,
      rangeToApply,
      options,
    );

    if (command) {
      await command.exec();
    }
  }

  async toggleListStyle(
    listStyleId: string | boolean = false,
    rangeToApply?: Editor.Selection.JsonRange,
    options: Editor.Edition.StylesCommandOptions = {},
  ) {
    if (this.stylesContext.debug) {
      logger.debug('StylesManager toggleListStyle', listStyleId, rangeToApply, options);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'TOGGLE_LIST',
      listStyleId,
      rangeToApply,
      options,
    );

    if (command) {
      await command.exec();
    }
  }
  // #endregion

  // #####################################
  // #region Document Styles
  // #####################################

  // #endregion

  // ################################################
  // #region Styles Status Check
  // ################################################
  checkSelectionStatus() {
    return this.selectionStatus.debounceCheckSelectionStatus();
  }
  // #endregion
}
